import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-text-primary mb-3">{title}</h2>
    <div className="text-text-secondary leading-relaxed space-y-3">{children}</div>
  </div>
);

const Terms = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="bg-neutral-bg min-h-screen">
      {/* Hero Banner */}
      <div className="bg-primary py-16 px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-white/70 text-sm uppercase tracking-widest mb-2">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Terms of Service</h1>
          <p className="text-white/70 text-sm">Last updated: {currentDate}</p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>

          <div className="bg-amber/10 border border-amber/20 rounded-2xl p-5 mb-10 text-sm text-amber-dark">
            <strong>Quick summary:</strong> By using Pickleball Insight Coach, you agree to use the app fairly, keep your account secure, and understand that we provide coaching tools — not medical or professional sports advice.
          </div>

          <Section title="1. Acceptance of Terms">
            <p>Welcome to Pickleball Insight Coach. By downloading, installing, or using our app, you agree to be bound by these Terms of Service. If you do not agree, please do not use the app.</p>
            <p>We may update these terms from time to time. When we do, we'll notify you in the app. Continued use after changes means you accept the updated terms.</p>
          </Section>

          <Section title="2. Who Can Use the App">
            <p>You must be at least 13 years old to use Pickleball Insight Coach. If you are under 18, you should have a parent or guardian review these terms with you.</p>
            <p>By using the app, you confirm that all information you provide is accurate and that you have the legal right to agree to these terms.</p>
          </Section>

          <Section title="3. Your Account">
            <p>You are responsible for keeping your login credentials secure. Do not share your account with others. If you believe your account has been compromised, contact us immediately at hello@pickleballinsightcoach.com.</p>
            <p>We reserve the right to suspend or terminate accounts that violate these terms or are involved in any abusive, fraudulent, or harmful activity.</p>
          </Section>

          <Section title="4. What the App Does">
            <p>Pickleball Insight Coach provides AI-powered video analysis, personalized drills, classroom lessons, and progress tracking to help you improve your pickleball game.</p>
            <p>Our tools are designed for recreational and competitive players and are intended for informational and training purposes only. The app does not provide medical advice. If you experience pain or injury, please consult a qualified medical professional before continuing play.</p>
          </Section>

          <Section title="5. Video & Content You Upload">
            <p>When you record and upload match videos, you grant us a limited license to process that footage solely for the purpose of generating your analysis results. We do not sell your video content to third parties.</p>
            <p>You are responsible for ensuring you have the consent of any other players visible in your recordings. Do not upload videos that include minors without appropriate parental consent.</p>
          </Section>

          <Section title="6. Subscriptions & Payments">
            <p>Pickleball Insight Coach is free to download. Some features may require a paid subscription. Subscription pricing and terms will be clearly displayed before any purchase is made.</p>
            <p>Subscriptions auto-renew unless cancelled at least 24 hours before the renewal date. You can manage or cancel your subscription at any time through your App Store or Google Play account settings.</p>
            <p>Refunds are handled according to the policies of Apple App Store or Google Play Store, as applicable.</p>
          </Section>

          <Section title="7. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the app for any unlawful purpose</li>
              <li>Attempt to reverse-engineer, hack, or tamper with the app</li>
              <li>Upload content that is offensive, harmful, or violates any third-party rights</li>
              <li>Share, resell, or commercially exploit any part of the app without our written permission</li>
              <li>Use automated tools to scrape or extract data from the app</li>
            </ul>
          </Section>

          <Section title="8. Intellectual Property">
            <p>All content within Pickleball Insight Coach — including the AI models, lesson videos, drill content, branding, and software — is owned by or licensed to us. You may not copy, reproduce, or distribute any part of the app without our express written consent.</p>
          </Section>

          <Section title="9. Disclaimers">
            <p>The app is provided "as is." While we work hard to keep everything running smoothly, we do not guarantee uninterrupted access or error-free performance. We are not liable for any injury, loss, or damage resulting from your use of the app or reliance on its content.</p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>To the fullest extent permitted by law, Pickleball Insight Coach and its team shall not be liable for any indirect, incidental, or consequential damages arising from your use of the app.</p>
          </Section>

          <Section title="11. Contact Us">
            <p>Questions about these terms? Reach out to us anytime:</p>
            <p><strong>Email:</strong> hello@pickleballinsightcoach.com</p>
            <p><strong>Mailing Address:</strong> Pickleball Insight Coach, 7250 Pierson Dr Mobile AL 36619</p>
          </Section>

        </motion.div>
      </div>
    </div>
  );
};

export default Terms;